/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import { memo, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import { useSelector, useDispatch } from 'react-redux'
import * as turf from '@turf/turf'
import {
  useRouter,
  PopetyMaps,
  useI18n,
  PrintIcon,
} from '@popety_io/popety-io-lib'
import { transformTileRequest } from '../../../../utils'

import {
  STYLE_PREFIX,
  MAPBOX_TOKEN,
  DEFAULT_MAPSTYLE,
} from '../../../../config'
import { estimateSelector, mapStyleSelector, changeMapStyle } from '../../redux'
import GeolocMapControls from './GeolocMapControls'
import { useEstimateMapLayers } from './EstimateMap.layers'
import {
  MapControllers,
  MapConfigByPage,
  buildOptions,
} from './EstimateMap.config'
import ControlMapStyleSwitch from './ControlMapStyleSwitch'

export type EstimateMapProps = {
  fullscreen?: boolean
}

const EstimateMap = ({ fullscreen }: EstimateMapProps) => {
  const [zoom, setZoom] = useState(18)
  const estimate = useSelector(estimateSelector)

  const { query, updateQuery } = useRouter()
  const [center, setCenter] = useState<any>(undefined)

  const { data } = useEstimateMapLayers()

  const dispatch = useDispatch()

  const mapStyle = useSelector(mapStyleSelector)

  const { t } = useI18n()

  useEffect(() => {
    if (estimate?.geoCenter) {
      setCenter(estimate?.geoCenter)
    }
  }, [estimate?.geoCenter])

  useEffect(() => {
    if (query.zoom) {
      setZoom(Number(query.zoom))
    }
  }, [query.zoom])

  useEffect(() => {
    updateQuery({ zoom })
  }, [])

  const options = buildOptions(t, mapStyle)

  const bbox = useMemo(() => {
    if (!estimate?.geoCenter) return undefined

    const point = turf.point([
      estimate?.geoCenter?.lon,
      estimate?.geoCenter?.lat,
    ])

    const buffered = turf.buffer(point, 10, {
      units: 'kilometers',
    })

    return turf.bbox(buffered)
  }, [estimate?.geoCenter])

  const minHeight = fullscreen
    ? 'calc(100vh - 160px)'
    : { xs: '400px', md: '40vh' }

  const mapProps: any = {
    zoom,
    bearing: 0,
    bearingSnap: 0,
    dragPan: true,
    dragRotate: true,
    maxZoom: 20,
    minZoom: 13,
    preserveDrawingBuffer: true,
    bbox,
    transformRequest: transformTileRequest as any,
  }

  const MapConfig = useMemo(
    () => MapConfigByPage[query.subTab ?? query.tab],
    [query.subTab, query.tab],
  )

  const mapConfigStyle = useMemo(() => {
    if (mapStyle) {
      const style = mapStyle.startsWith(STYLE_PREFIX)
        ? mapStyle.substring(STYLE_PREFIX.length)
        : mapStyle

      return `${STYLE_PREFIX}${style}`
    }
  }, [mapStyle])

  useEffect(() => {
    dispatch(changeMapStyle(MapConfig?.style || DEFAULT_MAPSTYLE))
  }, [MapConfig?.style, query.tab, query.subTab])

  return (
    <Box height="100%" minHeight={minHeight}>
      <PopetyMaps
        dimension="3D"
        token={MAPBOX_TOKEN}
        data={data}
        center={center}
        isDragPan
        bbox={bbox}
        style={mapConfigStyle}
        ClickComponent={MapConfig?.Content}
        {...mapProps}
        sx={{
          minHeight,
          maxHeight: fullscreen
            ? '100%'
            : { xs: 'calc(100% - 106px)', lg: '100%' },
          overflow: 'hidden',
        }}
        zoom={zoom}
        ofsYear={query?.historyLayer ?? undefined}
      >
        <GeolocMapControls center={center} zoom={zoom} />
        <MapControllers />
        <PrintIcon name={t('common.myAnalysis.fileName.details')} />
        <ControlMapStyleSwitch options={options.slice(0, 2)} />
      </PopetyMaps>
    </Box>
  )
}

export default memo(EstimateMap)
